import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Inbox, Loader2, Mail } from 'lucide-react';
import { cloudFunctions, type SupportTicketStatus, type SupportTicketSummary } from '@/lib/firebase';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

const FILTERS: Array<{ value: SupportTicketStatus | 'all'; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'answered', label: 'Answered' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

function replyHref(ticket: SupportTicketSummary): string {
  const subject = `Re: ${ticket.subject || 'Your Romulus support request'}`;
  const quoted = ticket.message.split('\n').map((line) => `> ${line}`).join('\n');
  return `mailto:${ticket.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`\n\n${quoted}`)}`;
}

export function SupportInboxPage() {
  const isAdmin = useIsAdmin();
  const [filter, setFilter] = useState<SupportTicketStatus | 'all'>('open');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['supportTickets'],
    queryFn: () => cloudFunctions.listSupportTickets({}),
    enabled: isAdmin === true,
  });

  const updateStatus = useMutation({
    mutationFn: (vars: { ticketId: string; status: SupportTicketStatus }) => cloudFunctions.updateSupportTicket(vars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supportTickets'] }),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not update ticket'),
  });

  const tickets = useMemo(
    () => (data?.tickets ?? []).filter((t) => filter === 'all' || t.status === filter),
    [data, filter],
  );
  const openCount = data?.tickets.filter((t) => t.status === 'open').length ?? 0;

  if (isAdmin === false) return <Navigate to="/dashboard/support" replace />;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Support Inbox</h1>
          <p className="text-gray-400 mt-1">{openCount} open ticket{openCount === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <Button key={f.value} size="sm" variant={filter === f.value ? 'default' : 'outline'} onClick={() => setFilter(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {!isAdmin || isLoading ? (
        <div className="flex items-center gap-3 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading tickets…</div>
      ) : error ? (
        <GlassCard><p className="text-red-400">{error instanceof Error ? error.message : 'Failed to load tickets'}</p></GlassCard>
      ) : tickets.length === 0 ? (
        <GlassCard>
          <div className="flex items-center gap-3 text-gray-400"><Inbox className="w-5 h-5" /> Nothing here.</div>
        </GlassCard>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <GlassCard key={ticket.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-white">{ticket.subject || '(no subject)'}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {ticket.email} · {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : 'unknown date'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {ticket.planId && <Badge variant="secondary">{ticket.planId}{ticket.subscriptionActive ? ' · paying' : ''}</Badge>}
                  <Badge variant={ticket.status === 'open' ? 'warning' : 'success'}>{ticket.status}</Badge>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm text-gray-300">{ticket.message}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {ticket.email && (
                  <a href={replyHref(ticket)}>
                    <Button size="sm"><Mail className="w-4 h-4 mr-2" /> Reply by email</Button>
                  </a>
                )}
                {ticket.status !== 'answered' && (
                  <Button size="sm" variant="outline" loading={updateStatus.isPending && updateStatus.variables?.ticketId === ticket.id}
                    onClick={() => updateStatus.mutate({ ticketId: ticket.id, status: 'answered' })}>
                    Mark answered
                  </Button>
                )}
                {ticket.status !== 'closed' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ ticketId: ticket.id, status: 'closed' })}>
                    Close
                  </Button>
                )}
                {ticket.status !== 'open' && (
                  <Button size="sm" variant="ghost" onClick={() => updateStatus.mutate({ ticketId: ticket.id, status: 'open' })}>
                    Reopen
                  </Button>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
